// Lightweight connection diagnostics for the upload UIs: round-trip latency
// (ping), a quick download/upload throughput probe against the backend, plus
// whatever the browser's Network Information API exposes.

import { API_BASE } from '../data/env';

const API = API_BASE;

export function connectionInfo() {
  const c =
    (typeof navigator !== 'undefined' &&
      (navigator.connection || navigator.mozConnection || navigator.webkitConnection)) ||
    null;
  if (!c) return null;
  return {
    effectiveType: c.effectiveType || '',
    downlinkMbps: typeof c.downlink === 'number' ? c.downlink : null,
    rttMs: typeof c.rtt === 'number' ? c.rtt : null,
    saveData: !!c.saveData,
  };
}

async function pingOnce() {
  const t = performance.now();
  await fetch(`${API}/health?t=${Date.now()}`, { cache: 'no-store' });
  return performance.now() - t;
}

export async function measurePing(samples = 4) {
  let best = Infinity;
  for (let i = 0; i < samples; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const ms = await pingOnce();
      if (ms < best) best = ms;
    } catch (_e) {
      /* ignore individual failures */
    }
  }
  return Number.isFinite(best) ? Math.round(best) : null;
}

// Streams the probe payload and times from the FIRST byte received, so the
// result reflects steady-state throughput rather than connection setup + the
// server's first-byte latency (which would crush the number on fast links).
export async function measureDownload(bytes = 8_000_000) {
  const res = await fetch(`${API}/netcheck/down?bytes=${bytes}&t=${Date.now()}`, { cache: 'no-store' });
  const reader = res.body && res.body.getReader ? res.body.getReader() : null;
  if (!reader) {
    const t = performance.now();
    const buf = await res.arrayBuffer();
    const dt = (performance.now() - t) / 1000;
    return dt > 0 ? (buf.byteLength * 8) / dt / 1e6 : null;
  }
  let received = 0;
  let start = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    if (!start) start = performance.now();
    received += value.length;
  }
  const dt = (performance.now() - start) / 1000;
  return dt > 0 ? (received * 8) / dt / 1e6 : null;
}

// NOTE: there is deliberately no synthetic upload probe. Posting a fixed blob
// and dividing bytes/time wildly overestimates on fast links because the body
// is absorbed into the OS send buffer + the CDN/ALB edge in milliseconds — it
// measures "time to hand off to the edge", not sustained end-to-end upload. The
// UI instead shows the REAL upload throughput observed from the actual S3
// multipart transfer (transferMonitor wire bytes), which is the number that
// actually matters for getting footage uploaded.

// Speed test: round-trip latency + a stream-timed download probe (both
// reliable). Upload speed is measured live from real transfers, not here.
export async function runSpeedTest() {
  const out = { ...(connectionInfo() || {}) };
  try {
    out.pingMs = await measurePing();
  } catch (_e) {
    /* ignore */
  }
  try {
    let down = await measureDownload(8_000_000);
    if (down && down > 40) {
      const big = await measureDownload(64_000_000).catch(() => null);
      if (big) down = Math.max(down, big);
    }
    out.downMbps = down;
  } catch (_e) {
    /* ignore */
  }
  out.at = Date.now();
  return out;
}
